import { API_HOST_V2, AUTHENTICATE_TOKEN_KEY } from 'app/config'
import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { jwtDecode } from 'jwt-decode'

import { AuthToken } from '../api'
import { authHTApi } from '../auth'

const apiv2Client = axios.create({
  baseURL: API_HOST_V2,
  headers: {},
})

export const setHeaderV2 = (authToken: AuthToken) => {
  if (!authToken) {
    return
  }
  apiv2Client.defaults.headers.common.Authorization = `${authToken.token_type} ${authToken.accessToken}`
}

function onResponse(response: AxiosResponse): AxiosResponse {
  return response.data
}

const refreshTokenIfNeed = async (): Promise<AuthToken | null> => {
  const storedAuthToken = JSON.parse(
    window.localStorage.getItem(AUTHENTICATE_TOKEN_KEY) || 'null',
  ) as AuthToken
  if (!storedAuthToken) {
    return null
  }
  const accessToken = jwtDecode(storedAuthToken.accessToken)
  const tokenExp = (accessToken.exp || 0) * 1000 - Date.now()
  // If the token is valid for more than 5 minutes, or expired for more than 3 hours, do not refresh
  if (tokenExp > 5 * 60 * 1000 || tokenExp < -3 * 60 * 60 * 1000) {
    return storedAuthToken
  }

  const payload = {
    userPoolName: 'clinic-admin',
    userName: accessToken.sub,
    refreshToken: storedAuthToken.refreshToken,
  }
  try {
    const result = await authHTApi.getUserAccessToken(payload)
    const { idToken: newIdToken, accessToken: newAccessToken } = result.data
    const newAuthToken: AuthToken = {
      token_type: 'Bearer',
      idToken: newIdToken,
      accessToken: newAccessToken,
      refreshToken: storedAuthToken.refreshToken,
    }
    setHeaderV2(newAuthToken)
    window.localStorage.setItem(
      AUTHENTICATE_TOKEN_KEY,
      JSON.stringify(newAuthToken),
    )
    return newAuthToken
  } catch (error: any) {
    console.error('error refresh token', error)
  }
  return null
}

async function onRequest(config: InternalAxiosRequestConfig) {
  const accessToken = await refreshTokenIfNeed()
  if (accessToken) {
    const authorizationToken = `Bearer ${accessToken?.accessToken}`
    config.headers.Authorization = authorizationToken
  }
  return config
}

const reqInterceptor = apiv2Client.interceptors.request.use(onRequest)
apiv2Client.interceptors.response.use(onResponse)

/**
 * NOTE: Storybook/Vitest実行時用
 *       jwtトークンを使用したonRequestを無効化する
 */
function initForTest() {
  apiv2Client.interceptors.request.eject(reqInterceptor)
}

if (window.localStorage.getItem(AUTHENTICATE_TOKEN_KEY)) {
  const authToken = JSON.parse(
    window.localStorage.getItem(AUTHENTICATE_TOKEN_KEY) || 'null',
  ) as AuthToken
  setHeaderV2(authToken)
}

export { apiv2Client, initForTest }
